// ✅ src/components/screens/EarnMoneyScreen.js
// ✅ FULL: Wallet + Referral + Stats + History + Withdraw
// ✅ UPDATE: referralLink now lands on HOMEPAGE (/?ref=...)
// ✅ FIX: referral history shows referredUid (not sellerUid)
// ✅ UPDATE: Withdraw can be ANY amount (no minimum)
// ✅ FIX: On withdraw request, move money from wallet.balance -> wallet.pending using Firestore transaction
// ✅ ADD: Writes a wallet transaction: WITHDRAW_REQUEST (DEBIT)
// ✅ UI FIX: Long “history/notifications” lists no longer push the page down
//    - Transactions / Referral history / Withdrawals are COLLAPSED by default
//    - Each has a “Show” button + scroll area (max height) when opened
// ✅ Mobile FIX: MoMo network select uses in-app bottom-sheet (no iOS popup)

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  serverTimestamp,
  setDoc,
  updateDoc,
  runTransaction,
  increment,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function money(n, cur = "GHS") {
  const num = Number(n ?? 0);
  if (Number.isNaN(num)) return `${cur} 0`;
  return `${cur} ${num.toLocaleString()}`;
}

function fmtDate(ts) {
  try {
    if (!ts) return "";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "";
  }
}

/* ---------------- small hooks/components ---------------- */
function useIsMobile(breakpoint = 860) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function MobileSheetSelect({
  label,
  value,
  onChange,
  options, // [{value,label}]
  disabled = false,
  placeholder = "Select…",
  buttonStyle,
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel =
    options.find((o) => String(o.value) === String(value))?.label || "";

  const close = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{
          ...buttonStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span style={{ color: selectedLabel ? "#0f172a" : "rgba(15,23,42,0.55)" }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ fontWeight: 1200, color: "#64748b" }}>▾</span>
      </button>

      {open ? (
        <div
          onClick={close}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              background: "#fff",
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
              padding: 12,
              borderTop: "1px solid rgba(15,23,42,0.10)",
              boxShadow: "0 -20px 60px rgba(15,23,42,0.15)",
              maxHeight: "70vh",
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div style={{ fontWeight: 1000 }}>{label || "Select"}</div>
              <button
                type="button"
                onClick={close}
                style={{
                  border: "1px solid rgba(15,23,42,0.12)",
                  background: "#fff",
                  padding: "10px 12px",
                  borderRadius: 14,
                  fontWeight: 1000,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {options.map((opt) => {
                const active = String(opt.value) === String(value);
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      close();
                    }}
                    style={{
                      border: "1px solid rgba(15,23,42,0.10)",
                      background: active ? "#0f172a" : "#fff",
                      color: active ? "#fff" : "#0f172a",
                      borderRadius: 14,
                      padding: "12px 12px",
                      fontWeight: 950,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function CollapsibleCard({
  title,
  subtitle,
  count,
  open,
  setOpen,
  children,
  maxHeight = 360,
  S,
}) {
  return (
    <div style={S.card}>
      <div style={S.cardHead}>
        <div style={{ minWidth: 0 }}>
          <div style={S.sectionTitle}>{title}</div>
          {subtitle ? <div style={S.smallSub}>{subtitle}</div> : null}
        </div>

        <button
          type="button"
          onClick={() => setOpen(!open)}
          style={S.foldBtn}
          aria-expanded={open}
        >
          {open ? "Hide" : "Show"}
          {typeof count === "number" ? ` (${count})` : ""}
        </button>
      </div>

      {open ? (
        <div style={{ ...S.scrollArea, maxHeight }}>{children}</div>
      ) : (
        <div style={S.smallMuted}>Hidden to keep the page clean 👌</div>
      )}
    </div>
  );
}

/* ---------------- referral helpers ---------------- */
function genReferralCode(uid) {
  const a = (uid || "").slice(0, 6).toUpperCase();
  const b = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `REF-${a}-${b}`;
}

async function ensureUserDocAndReferral(currentUid) {
  const uref = doc(db, "users", currentUid);
  const snap = await getDoc(uref);

  if (!snap.exists()) {
    const code = genReferralCode(currentUid);
    await setDoc(
      uref,
      {
        referralCode: code,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { created: true, referralCode: code };
  }

  const data = snap.data() || {};
  const code = safe(data.referralCode);

  if (!code) {
    const newCode = genReferralCode(currentUid);
    await updateDoc(uref, { referralCode: newCode, updatedAt: serverTimestamp() });
    return { created: false, referralCode: newCode };
  }

  return { created: false, referralCode: code };
}

export default function EarnMoneyScreen({ goBack }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile(860);

  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState("");

  const [wallet, setWallet] = useState({
    balance: 0,
    pending: 0,
    totalEarned: 0,
    totalWithdrawn: 0,
    currency: "GHS",
  });

  const [referralCode, setReferralCode] = useState("");
  const [txs, setTxs] = useState([]);
  const [refHistory, setRefHistory] = useState([]);
  const [withdrawHistory, setWithdrawHistory] = useState([]);

  const [refStats, setRefStats] = useState({
    pending: 0,
    qualified: 0,
    rewarded: 0,
    cancelled: 0,
  });

  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState("");
  const [err, setErr] = useState("");

  const [wAmount, setWAmount] = useState("");
  const [wNetwork, setWNetwork] = useState("MTN");
  const [wNumber, setWNumber] = useState("");
  const [wName, setWName] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  // ✅ collapsers (hidden by default so they don't push content down)
  const [openTx, setOpenTx] = useState(false);
  const [openRefs, setOpenRefs] = useState(false);
  const [openWithdraws, setOpenWithdraws] = useState(false);

  // ✅ ONLY CHANGE: referral link now lands on homepage
  const referralLink = useMemo(() => {
    if (!referralCode) return "";
    return `${window.location.origin}/?ref=${encodeURIComponent(referralCode)}`;
  }, [referralCode]);

  const showToastFn = (m) => {
    setToast(m);
    window.clearTimeout(showToastFn._t);
    showToastFn._t = window.setTimeout(() => setToast(""), 1600);
  };

  const copyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      showToastFn("Copied ✓");
      setTimeout(() => setCopied(false), 900);
    } catch {
      showToastFn("Copy failed");
    }
  };

  const shareLink = async () => {
    if (!referralLink) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Referral Link",
          text: "Sign up with my referral link:",
          url: referralLink,
        });
      } else {
        await navigator.clipboard.writeText(referralLink);
        showToastFn("Copied (share not supported)");
      }
    } catch {}
  };

  async function loadAll(currentUid) {
    // wallet
    try {
      const walletRef = doc(db, "wallets", currentUid);
      const walletSnap = await getDoc(walletRef);

      if (walletSnap.exists()) {
        const w = walletSnap.data() || {};
        setWallet({
          balance: Number(w.balance ?? 0),
          pending: Number(w.pending ?? 0),
          totalEarned: Number(w.totalEarned ?? 0),
          totalWithdrawn: Number(w.totalWithdrawn ?? 0),
          currency: safe(w.currency) || "GHS",
        });
      } else {
        setWallet({
          balance: 0,
          pending: 0,
          totalEarned: 0,
          totalWithdrawn: 0,
          currency: "GHS",
        });
      }
    } catch {
      setWallet({
        balance: 0,
        pending: 0,
        totalEarned: 0,
        totalWithdrawn: 0,
        currency: "GHS",
      });
    }

    // ensure user doc + referralCode
    let ensuredCode = "";
    try {
      const ensured = await ensureUserDocAndReferral(currentUid);
      ensuredCode = safe(ensured?.referralCode);
    } catch {}

    // user doc -> referralCode
    try {
      const userSnap = await getDoc(doc(db, "users", currentUid));
      if (userSnap.exists()) {
        const u = userSnap.data() || {};
        const rc = safe(u.referralCode) || ensuredCode;
        setReferralCode(rc);
      } else {
        setReferralCode(ensuredCode || "");
      }
    } catch {
      setReferralCode(ensuredCode || "");
    }

    // tx list
    try {
      const txQ = query(
        collection(db, "wallets", currentUid, "transactions"),
        orderBy("createdAt", "desc"),
        limit(40)
      );
      const txSnap = await getDocs(txQ);
      setTxs(txSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      setTxs([]);
    }

    // referral stats + referral history
    try {
      const rQ = query(collection(db, "referrals"), where("referrerUid", "==", currentUid));
      const rSnap = await getDocs(rQ);

      let pending = 0;
      let qualified = 0;
      let rewarded = 0;
      let cancelled = 0;

      const rows = rSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      rows.forEach((x) => {
        const s = safe(x.status).toUpperCase() || "UNKNOWN";
        if (s === "PENDING") pending += 1;
        else if (s === "QUALIFIED") qualified += 1;
        else if (s === "REWARDED") rewarded += 1;
        else if (s === "CANCELLED") cancelled += 1;
      });

      rows.sort((a, b) => {
        const ta = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
        const tb = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
        return tb - ta;
      });

      setRefStats({ pending, qualified, rewarded, cancelled });
      setRefHistory(rows);
    } catch {
      setRefStats({ pending: 0, qualified: 0, rewarded: 0, cancelled: 0 });
      setRefHistory([]);
    }

    // withdrawal history
    try {
      const wQ = query(
        collection(db, "withdrawals"),
        where("uid", "==", currentUid),
        orderBy("createdAt", "desc"),
        limit(50)
      );
      const wSnap = await getDocs(wQ);
      setWithdrawHistory(wSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      setWithdrawHistory([]);
    }
  }

  useEffect(() => {
    let alive = true;

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!alive) return;

      setErr("");
      setLoading(true);

      try {
        if (!u) {
          setUid("");
          setReferralCode("");
          setWallet({
            balance: 0,
            pending: 0,
            totalEarned: 0,
            totalWithdrawn: 0,
            currency: "GHS",
          });
          setTxs([]);
          setRefHistory([]);
          setWithdrawHistory([]);
          setRefStats({ pending: 0, qualified: 0, rewarded: 0, cancelled: 0 });
          setLoading(false);
          return;
        }

        setUid(u.uid);
        await loadAll(u.uid);
      } catch {
        setErr("Could not load this page. Refresh and try again.");
      } finally {
        setLoading(false);
      }
    });

    return () => {
      alive = false;
      unsub();
    };
  }, []);

  const canWithdraw = useMemo(() => {
    const amt = Number(wAmount || 0);
    return (
      uid &&
      !Number.isNaN(amt) &&
      amt > 0 &&
      amt <= Number(wallet.balance || 0) &&
      safe(wNumber) &&
      safe(wName)
    );
  }, [uid, wAmount, wNumber, wName, wallet.balance]);

  const submitWithdraw = async () => {
    if (!canWithdraw) {
      showToastFn("Check amount and details");
      return;
    }

    setWithdrawing(true);
    try {
      const amt = Number(wAmount);
      const walletRef = doc(db, "wallets", uid);

      await runTransaction(db, async (tx) => {
        const wSnap = await tx.get(walletRef);
        const w = wSnap.exists() ? wSnap.data() || {} : {};

        const balance = Number(w.balance ?? 0);
        if (!Number.isFinite(balance) || balance < amt) {
          throw new Error("Not enough balance");
        }

        const newWithdrawalRef = doc(collection(db, "withdrawals"));
        tx.set(newWithdrawalRef, {
          uid,
          amount: amt,
          currency: safe(w.currency) || "GHS",
          method: "MOMO",
          momoNetwork: safe(wNetwork),
          momoNumber: safe(wNumber),
          momoName: safe(wName),
          status: "REQUESTED",
          createdAt: serverTimestamp(),
          processedAt: null,
          processedBy: null,
          note: "",
        });

        tx.update(walletRef, {
          balance: increment(-amt),
          pending: increment(amt),
          updatedAt: serverTimestamp(),
        });

        const txRef = doc(collection(db, "wallets", uid, "transactions"));
        tx.set(txRef, {
          type: "WITHDRAW_REQUEST",
          direction: "DEBIT",
          amount: amt,
          currency: safe(w.currency) || "GHS",
          status: "REQUESTED",
          refId: newWithdrawalRef.id,
          createdAt: serverTimestamp(),
        });
      });

      showToastFn("Withdrawal requested ✅");
      setWAmount("");
      await loadAll(uid);
    } catch (e) {
      showToastFn(e?.message || "Withdraw failed");
    } finally {
      setWithdrawing(false);
    }
  };

  const S = useMemo(() => {
    return {
      page: {
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 60%, #ffffff 100%)",
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
        color: "#0f172a",
      },
      container: { width: "100%", maxWidth: 1100, margin: "0 auto", padding: 16 },
      topBar: {
        position: "sticky",
        top: 0,
        zIndex: 50,
        backdropFilter: "blur(10px)",
        background: "rgba(248, 250, 252, 0.86)",
        borderBottom: "1px solid rgba(15,23,42,0.08)",
      },
      topInner: {
        width: "100%",
        maxWidth: 1100,
        margin: "0 auto",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      },
      hTitle: { fontSize: 16, fontWeight: 1200, letterSpacing: "-0.02em" },
      hSub: { fontSize: 12, color: "#64748b", marginTop: 2 },
      pill: {
        padding: "8px 10px",
        borderRadius: 999,
        background: "#0f172a",
        color: "white",
        fontWeight: 1000,
        fontSize: 12,
        whiteSpace: "nowrap",
      },

      grid: {
        display: "grid",
        gap: 14,
        gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr",
        alignItems: "start",
        marginTop: 14,
      },

      card: {
        background: "#fff",
        border: "1px solid rgba(15,23,42,0.08)",
        borderRadius: 18,
        boxShadow: "0 14px 40px rgba(15,23,42,0.06)",
        padding: 14,
        minWidth: 0,
      },

      cardHead: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 10,
      },

      sectionTitle: { fontSize: 13, fontWeight: 1200 },
      small: { fontSize: 13, color: "#475569", lineHeight: 1.6 },
      smallSub: { fontSize: 12, color: "#64748b", fontWeight: 900, marginTop: 3 },
      smallMuted: {
        fontSize: 12,
        color: "#64748b",
        fontWeight: 900,
        paddingTop: 2,
      },

      foldBtn: {
        border: "1px solid rgba(15,23,42,0.12)",
        background: "#fff",
        padding: "9px 10px",
        borderRadius: 14,
        fontWeight: 1100,
        cursor: "pointer",
        fontSize: 12,
        whiteSpace: "nowrap",
      },

      scrollArea: {
        overflow: "auto",
        paddingRight: 4,
        display: "grid",
        gap: 8,
      },

      statsRow: { display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" },
      stat: {
        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
        border: "1px solid rgba(15,23,42,0.08)",
        borderRadius: 16,
        padding: 12,
        minWidth: 0,
      },
      statLabel: { fontSize: 12, color: "#64748b", fontWeight: 1000 },
      statValue: { fontSize: 18, fontWeight: 1300, marginTop: 4 },

      refBox: {
        border: "1px solid rgba(15,23,42,0.10)",
        borderRadius: 16,
        padding: 12,
        background: "#fff",
        display: "grid",
        gap: 10,
      },
      refRow: {
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 10,
        alignItems: "center",
      },

      input: {
        width: "100%",
        border: "1px solid rgba(15,23,42,0.12)",
        borderRadius: 14,
        padding: "12px 12px",
        fontSize: 13,
        fontWeight: 900,
        outline: "none",
        background: "#f8fafc",
      },

      btn: {
        border: "1px solid rgba(15,23,42,0.14)",
        background: "#fff",
        padding: "11px 12px",
        borderRadius: 14,
        fontWeight: 1100,
        cursor: "pointer",
        fontSize: 13,
      },
      primary: {
        border: "none",
        background: "#0f172a",
        color: "#fff",
        padding: "11px 12px",
        borderRadius: 14,
        fontWeight: 1200,
        cursor: "pointer",
        fontSize: 13,
      },

      txItem: {
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: "10px 10px",
        borderRadius: 14,
        border: "1px solid rgba(15,23,42,0.08)",
        background: "#fff",
      },
      txLeft: { display: "grid", gap: 2, minWidth: 0 },
      txTitle: { fontSize: 13, fontWeight: 1100 },
      txMeta: { fontSize: 12, color: "#64748b" },
      txAmount: { fontSize: 13, fontWeight: 1200, whiteSpace: "nowrap" },

      alertErr: {
        border: "1px solid rgba(239,68,68,0.35)",
        background: "rgba(239,68,68,0.06)",
        color: "#b91c1c",
        padding: "10px 12px",
        borderRadius: 14,
        fontWeight: 900,
        fontSize: 13,
        marginTop: 14,
      },

      toast: {
        position: "fixed",
        left: "50%",
        bottom: 18,
        transform: "translateX(-50%)",
        background: "#0f172a",
        color: "#fff",
        padding: "10px 12px",
        borderRadius: 999,
        fontWeight: 1000,
        fontSize: 13,
        boxShadow: "0 18px 50px rgba(15,23,42,0.22)",
        zIndex: 9999,
      },

      badge: {
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: 999,
        border: "1px solid rgba(15,23,42,0.10)",
        fontSize: 12,
        fontWeight: 900,
        background: "rgba(248,250,252,0.9)",
      },

      // tiny layout help for very small screens
      responsive: `
        * { box-sizing: border-box; }
        @media (max-width: 420px) { .earn-stats { grid-template-columns: 1fr !important; } }
      `,
    };
  }, [isMobile]);

  const isLoggedIn = Boolean(uid);

  const networkOptions = useMemo(
    () => [
      { value: "MTN", label: "MTN" },
      { value: "TELECEL", label: "TELECEL" },
      { value: "AIRTELTIGO", label: "AIRTELTIGO" },
    ],
    []
  );

  if (loading) {
    return (
      <div style={S.page}>
        <style>{S.responsive}</style>
        <div style={S.topBar}>
          <div style={S.topInner}>
            <div>
              <div style={S.hTitle}>Earnings</div>
              <div style={S.hSub}>Loading…</div>
            </div>
            <div style={S.pill}>GHS 7 / paid signup</div>
          </div>
        </div>
        <div style={S.container}>
          <div style={S.card}>Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{S.responsive}</style>
      {toast ? <div style={S.toast}>{toast}</div> : null}

      <div style={S.topBar}>
        <div style={S.topInner}>
          <div>
            <div style={S.hTitle}>Earnings</div>
            <div style={S.hSub}>Refer sellers who pay subscription and earn rewards.</div>
          </div>
          <div style={S.pill}>Reward: GHS 7 / paid signup</div>
        </div>
      </div>

      <div style={S.container}>
        {err ? <div style={S.alertErr}>{err}</div> : null}

        <div style={S.grid}>
          {/* LEFT */}
          <div style={{ display: "grid", gap: 14 }}>
            <CollapsibleCard
              title="Recent wallet transactions"
              subtitle="Tap Show to view and scroll"
              count={txs.length}
              open={openTx}
              setOpen={setOpenTx}
              maxHeight={380}
              S={S}
            >
              {txs.length === 0 ? (
                <div style={S.small}>No transactions yet.</div>
              ) : (
                txs.map((t) => {
                  const type = safe(t.type || "TRANSACTION").replaceAll("_", " ");
                  const dir = safe(t.direction || "");
                  const amt = Number(t.amount ?? 0);
                  const sign = dir === "DEBIT" ? "-" : "+";
                  return (
                    <div key={t.id} style={S.txItem}>
                      <div style={S.txLeft}>
                        <div style={S.txTitle}>{type}</div>
                        <div style={S.txMeta}>
                          {safe(t.status || "")} • {fmtDate(t.createdAt)}
                        </div>
                      </div>
                      <div style={S.txAmount}>
                        {sign} {money(amt, safe(t.currency) || wallet.currency)}
                      </div>
                    </div>
                  );
                })
              )}
            </CollapsibleCard>

            <CollapsibleCard
              title="Referral history"
              subtitle="Tap Show to view and scroll"
              count={refHistory.length}
              open={openRefs}
              setOpen={setOpenRefs}
              maxHeight={420}
              S={S}
            >
              {refHistory.length === 0 ? (
                <div style={S.small}>No referrals yet.</div>
              ) : (
                refHistory.slice(0, 50).map((r) => {
                  const status = safe(r.status || "PENDING").toUpperCase();
                  const sellerName =
                    safe(r.sellerStoreName) || safe(r.sellerName) || "New user";
                  const amt = Number(r.rewardAmount ?? 7);

                  return (
                    <div key={r.id} style={S.txItem}>
                      <div style={S.txLeft}>
                        <div style={S.txTitle}>
                          {sellerName} <span style={S.badge}>{status}</span>
                        </div>
                        <div style={S.txMeta}>
                          referredUid: {safe(r.referredUid).slice(0, 10)}… •{" "}
                          {fmtDate(r.updatedAt || r.createdAt)}
                        </div>
                      </div>
                      <div style={S.txAmount}>{money(amt, safe(r.currency) || "GHS")}</div>
                    </div>
                  );
                })
              )}
            </CollapsibleCard>

            <CollapsibleCard
              title="Your withdrawal requests"
              subtitle="Tap Show to view and scroll"
              count={withdrawHistory.length}
              open={openWithdraws}
              setOpen={setOpenWithdraws}
              maxHeight={420}
              S={S}
            >
              {withdrawHistory.length === 0 ? (
                <div style={S.small}>No withdrawals yet.</div>
              ) : (
                withdrawHistory.map((w) => (
                  <div key={w.id} style={S.txItem}>
                    <div style={S.txLeft}>
                      <div style={S.txTitle}>
                        {safe(w.status || "REQUESTED")} •{" "}
                        {money(w.amount, safe(w.currency) || "GHS")}
                      </div>
                      <div style={S.txMeta}>
                        {safe(w.momoNetwork)} • {safe(w.momoNumber)} • {safe(w.momoName)} •{" "}
                        {fmtDate(w.createdAt)}
                      </div>
                    </div>
                    <div style={S.txAmount}>{safe(w.method || "MOMO")}</div>
                  </div>
                ))
              )}
            </CollapsibleCard>
          </div>

          {/* RIGHT */}
          <div style={{ display: "grid", gap: 14 }}>
            <div style={S.card}>
              <div style={S.sectionTitle}>Wallet</div>
              <div className="earn-stats" style={S.statsRow}>
                <div style={S.stat}>
                  <div style={S.statLabel}>Available</div>
                  <div style={S.statValue}>{money(wallet.balance, wallet.currency)}</div>
                </div>
                <div style={S.stat}>
                  <div style={S.statLabel}>Pending</div>
                  <div style={S.statValue}>{money(wallet.pending, wallet.currency)}</div>
                </div>
              </div>

              <div style={{ height: 10 }} />
              <div style={S.small}>
                Total earned: <b>{money(wallet.totalEarned, wallet.currency)}</b>
                <br />
                Total withdrawn: <b>{money(wallet.totalWithdrawn, wallet.currency)}</b>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.sectionTitle}>Referrals</div>

              <div className="earn-stats" style={S.statsRow}>
                <div style={S.stat}>
                  <div style={S.statLabel}>Pending</div>
                  <div style={S.statValue}>{refStats.pending}</div>
                </div>
                <div style={S.stat}>
                  <div style={S.statLabel}>Qualified</div>
                  <div style={S.statValue}>{refStats.qualified}</div>
                </div>
                <div style={S.stat}>
                  <div style={S.statLabel}>Rewarded</div>
                  <div style={S.statValue}>{refStats.rewarded}</div>
                </div>
                <div style={S.stat}>
                  <div style={S.statLabel}>Cancelled</div>
                  <div style={S.statValue}>{refStats.cancelled}</div>
                </div>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.sectionTitle}>Your referral link</div>

              {!isLoggedIn ? (
                <div style={S.small}>Please log in to see your referral link.</div>
              ) : !referralCode ? (
                <div style={S.small}>No referral code yet. Refresh and try again.</div>
              ) : (
                <div style={S.refBox}>
                  <div style={S.refRow}>
                    <input style={S.input} value={referralLink} readOnly />
                    <button style={S.btn} onClick={copyLink}>
                      {copied ? "Copied ✓" : "Copy"}
                    </button>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button style={S.primary} onClick={shareLink}>
                      Share
                    </button>
                    <button
                      style={S.btn}
                      onClick={() => {
                        showToastFn("Refreshing…");
                        loadAll(uid).catch(() => {});
                      }}
                    >
                      Refresh
                    </button>
                  </div>

                  <div style={S.small}>
                    Referrals must <b>sign up and pay</b> before rewards are added.
                  </div>
                </div>
              )}
            </div>

            <div style={S.card}>
              <div style={S.sectionTitle}>Withdraw</div>

              <div style={S.small}>
                Available: <b>{money(wallet.balance, wallet.currency)}</b>
              </div>

              <div style={{ height: 10 }} />

              <div style={{ display: "grid", gap: 10 }}>
                <input
                  style={S.input}
                  placeholder="Amount (e.g. 10)"
                  value={wAmount}
                  onChange={(e) => setWAmount(e.target.value)}
                  inputMode="numeric"
                />

                {isMobile ? (
                  <MobileSheetSelect
                    label="Network"
                    value={wNetwork}
                    onChange={(v) => setWNetwork(String(v))}
                    options={networkOptions}
                    placeholder="Choose network"
                    buttonStyle={S.input}
                  />
                ) : (
                  <select
                    style={S.input}
                    value={wNetwork}
                    onChange={(e) => setWNetwork(e.target.value)}
                  >
                    <option value="MTN">MTN</option>
                    <option value="TELECEL">TELECEL</option>
                    <option value="AIRTELTIGO">AIRTELTIGO</option>
                  </select>
                )}

                <input
                  style={S.input}
                  placeholder="MoMo number (e.g. 024xxxxxxx)"
                  value={wNumber}
                  onChange={(e) => setWNumber(e.target.value)}
                />

                <input
                  style={S.input}
                  placeholder="MoMo name (e.g. Ama Mensah)"
                  value={wName}
                  onChange={(e) => setWName(e.target.value)}
                />

                <button
                  style={{
                    ...S.primary,
                    opacity: canWithdraw && !withdrawing ? 1 : 0.5,
                    cursor: canWithdraw && !withdrawing ? "pointer" : "not-allowed",
                  }}
                  onClick={submitWithdraw}
                  disabled={!canWithdraw || withdrawing}
                >
                  {withdrawing ? "Requesting..." : "Request Withdrawal"}
                </button>

                <div style={{ ...S.small, fontSize: 12 }}>
                  Withdrawals are processed by admin. Your wallet updates after payment.
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button style={S.btn} onClick={goBack || (() => navigate(-1))}>
                ← Back
              </button>
              <button style={S.btn} onClick={() => navigate("/browse")}>
                Browse Stores
              </button>
              <button
                style={S.btn}
                onClick={() => {
                  showToastFn("Refreshing…");
                  if (uid) loadAll(uid).catch(() => {});
                }}
              >
                Force Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
